/**
 * Phrases course progression (ფრაზები — PHRASES.md § 3). Pure: course tree +
 * completions in, per-level lesson states out. No React, no Supabase, so
 * every rule below is unit-tested in `progression.test.ts`.
 *
 * Rules:
 *   - One unit per CEFR level (`phr-a1` … `phr-c2`); skills are internal only.
 *   - Inside a level the order is strict: lesson N opens when lesson N−1 is
 *     done. Lesson 1 of EVERY level is open from day one, so a B2 learner
 *     never has to grind A1.
 *   - Levels never lock each other.
 *   - `next` = the first open, not-done lesson of the level the learner
 *     worked in last (most recent completion). When that level has nothing
 *     open left, the search moves on to the levels after it, then wraps to
 *     A1 — finishing B2 points at C1, not back at A1. No phrases progress
 *     yet → A1.
 *   - `golden` = done with a perfect run (same as the main tree).
 *   - A review lesson (every 5th) is the one with no phrase cards.
 */

export const CEFR_CODES = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'] as const;
export type CefrCode = (typeof CEFR_CODES)[number];

export type PhraseLessonState = 'locked' | 'open' | 'next' | 'done' | 'golden';

/** The slice of a course tree this module reads. `CourseTree` (content/course.ts)
 *  satisfies it structurally; tests and the demo route pass small literals. */
export interface PhrasesTreeInput {
  units: ReadonlyArray<{
    id: string;
    title: string;
    resolvedSkills: ReadonlyArray<{
      resolvedLessons: ReadonlyArray<{
        id: string;
        title: string;
        tile_en?: string;
        exercises: ReadonlyArray<{ type: string }>;
      }>;
    }>;
  }>;
}

/** What progression needs from a completion row. */
export interface PhraseCompletion {
  perfect: boolean;
  /** ms since epoch — the most recent one picks the "level you worked in last". */
  completedAt: number;
}

export interface PhraseLessonView {
  id: string;
  /** 1-based position inside its level ("lesson N of A1"). */
  n: number;
  /** Georgian lesson title. */
  title: string;
  /** The lesson's catchiest phrase (shown on its tile); null when the lesson has none. */
  tileEn: string | null;
  isReview: boolean;
  /** New phrases this lesson teaches (its phrase cards; 0 for a review). */
  phraseCount: number;
  /** Teaching lessons since the previous review (what a review lesson covers); 0 otherwise. */
  reviewScope: number;
  state: PhraseLessonState;
  /** Pro-gated for THIS learner right now. Only decorates open/next lessons —
   *  a tap still opens /learn/:id, which shows the paywall. */
  proLocked: boolean;
}

export interface PhraseLevelView {
  unitId: string;
  code: CefrCode;
  /** Georgian level name (the unit title, e.g. „საწყისი“). */
  title: string;
  lessons: PhraseLessonView[];
  /** Lessons done (done + golden). */
  done: number;
  total: number;
  phrasesLearned: number;
  phrasesTotal: number;
}

export interface PhrasesView {
  levels: PhraseLevelView[];
  nextLessonId: string | null;
  /** New phrases of every done teaching lesson. */
  phrasesLearned: number;
  /** New phrases in the whole course (480 when the course is complete). */
  phrasesTotal: number;
}

export interface PhrasesViewOptions {
  /** True when this lesson needs Pro the learner doesn't have. Callers pass
   *  `() => false` while the subscription read is in flight (never padlock a
   *  subscriber for a frame). */
  isProLocked?: (lessonId: string) => boolean;
}

const UNIT_CODE_RE = /(?:^|-)(a1|a2|b1|b2|c1|c2)$/iu;

/** The level code of a phrases unit: from its id (`phr-b1` → B1), else by position. */
export function levelCodeOf(unitId: string, index: number): CefrCode {
  const m = UNIT_CODE_RE.exec(unitId);
  if (m?.[1]) return m[1].toUpperCase() as CefrCode;
  return CEFR_CODES[Math.min(index, CEFR_CODES.length - 1)] ?? 'A1';
}

function phraseCardCount(exercises: ReadonlyArray<{ type: string }>): number {
  let n = 0;
  for (const ex of exercises) if (ex.type === 'phrase_intro') n += 1;
  return n;
}

export function buildPhrasesView(
  tree: PhrasesTreeInput,
  completions: Readonly<Record<string, PhraseCompletion>>,
  options: PhrasesViewOptions = {},
): PhrasesView {
  const isProLocked = options.isProLocked ?? (() => false);

  // Pass 1 — states without `next`, and the level worked in last.
  let lastLevel = -1;
  let lastAt = -Infinity;
  const levels: PhraseLevelView[] = tree.units.map((unit, levelIndex) => {
    const flat = unit.resolvedSkills.flatMap((s) => s.resolvedLessons);
    let prevDone = true; // lesson 1 of every level is open
    let sinceReview = 0;
    let done = 0;
    let phrasesLearned = 0;
    let phrasesTotal = 0;
    const lessons = flat.map((lesson, i): PhraseLessonView => {
      const completion = completions[lesson.id];
      const phraseCount = phraseCardCount(lesson.exercises);
      const isReview = phraseCount === 0;
      const reviewScope = isReview ? sinceReview : 0;
      sinceReview = isReview ? 0 : sinceReview + 1;
      const state: PhraseLessonState = completion
        ? completion.perfect
          ? 'golden'
          : 'done'
        : prevDone
          ? 'open'
          : 'locked';
      if (completion) {
        done += 1;
        phrasesLearned += phraseCount;
        if (completion.completedAt > lastAt) {
          lastAt = completion.completedAt;
          lastLevel = levelIndex;
        }
      }
      phrasesTotal += phraseCount;
      prevDone = Boolean(completion);
      return {
        id: lesson.id,
        n: i + 1,
        title: lesson.title,
        tileEn: lesson.tile_en ?? null,
        isReview,
        phraseCount,
        reviewScope,
        state,
        proLocked: isProLocked(lesson.id),
      };
    });
    return {
      unitId: unit.id,
      code: levelCodeOf(unit.id, levelIndex),
      title: unit.title,
      lessons,
      done,
      total: lessons.length,
      phrasesLearned,
      phrasesTotal,
    };
  });

  // Pass 2 — `next`: the level worked in last, then the ones after it, then wrap.
  let nextLessonId: string | null = null;
  const start = Math.max(0, lastLevel);
  for (let k = 0; k < levels.length && !nextLessonId; k++) {
    const level = levels[(start + k) % levels.length];
    const lesson = level?.lessons.find((l) => l.state === 'open');
    if (lesson) {
      lesson.state = 'next';
      nextLessonId = lesson.id;
    }
  }

  return {
    levels,
    nextLessonId,
    phrasesLearned: levels.reduce((s, l) => s + l.phrasesLearned, 0),
    phrasesTotal: levels.reduce((s, l) => s + l.phrasesTotal, 0),
  };
}
